import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * How the palette reaches the browser's TOP LAYER (PLAN D4: above every
 * z-index and every open app modal):
 *   'popover' — `popover="manual"` + showPopover() (WebView2, Safari ≥ 17,
 *               evergreen). Preferred: showModal() also makes the whole page
 *               inert, and that style recalculation measured 5–9 ms per open
 *               in the e2e app — most of the 16 ms open budget (§3.3). The
 *               palette keeps focus itself (Tab / Shift+Tab stay inside, a
 *               click outside closes), so inertness is not needed for it.
 *   'dialog'  — a native <dialog> with showModal() (Safari 15.4–16).
 *   'portal'  — a fixed portal with `--z-palette` above `--z-modal`.
 */
export function topLayerMode() {
  if (typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function') return 'popover';
  if (typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function') return 'dialog';
  return 'portal';
}

/** Is there a top layer at all (popover or modal dialog)? */
export function supportsTopLayer() {
  return topLayerMode() !== 'portal';
}

/**
 * The palette's surface. A top-layer element stays mounted while closed
 * (hidden by the UA, not in the top layer), so opening is one call on
 * existing DOM rather than a fresh mount of the list and its scroller. The
 * native Esc `cancel` of a <dialog> is prevented: the palette's own Esc is
 * two-step (sub-menu / chip first). A click on the backdrop closes.
 */
export default function PaletteDialog({ open, onDismiss, labelledBy, children }) {
  const ref = useRef(null);
  const shown = useRef(false);
  const mode = topLayerMode();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el || mode === 'portal') return undefined;
    if (mode === 'popover') {
      try {
        if (!shown.current) el.showPopover();
        shown.current = true;
      } catch {
        /* not connected */
      }
      return () => {
        try {
          if (shown.current) el.hidePopover();
        } catch {
          /* already hidden */
        }
        shown.current = false;
      };
    }
    if (!el.open) {
      try {
        el.showModal();
      } catch {
        /* already open / not connected */
      }
    }
    const onCancel = (e) => e.preventDefault();
    el.addEventListener('cancel', onCancel);
    return () => {
      el.removeEventListener('cancel', onCancel);
      if (el.open) el.close();
    };
  }, [open, mode]);

  if (!open && mode === 'portal') return null;

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onDismiss?.();
  };
  const frame = (
    <div className="palette-frame" onMouseDown={onBackdrop}>
      {children}
    </div>
  );

  if (mode === 'popover') {
    return createPortal(
      <div
        ref={ref}
        popover="manual"
        className="palette-dialog palette-dialog--popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-palette-root=""
        data-open={open ? 'true' : 'false'}
        onMouseDown={onBackdrop}
      >
        {frame}
      </div>,
      document.body
    );
  }
  if (mode === 'dialog') {
    return createPortal(
      <dialog ref={ref} className="palette-dialog" aria-labelledby={labelledBy} data-palette-root="" data-open={open ? 'true' : 'false'} onMouseDown={onBackdrop}>
        {frame}
      </dialog>,
      document.body
    );
  }
  return createPortal(
    <div className="palette-dialog palette-dialog--fallback" role="dialog" aria-modal="true" aria-labelledby={labelledBy} data-palette-root="" onMouseDown={onBackdrop}>
      {frame}
    </div>,
    document.body
  );
}
